import i18n from 'i18next';

const lang = () => i18n?.language || (typeof navigator !== 'undefined' ? navigator.language : 'en-US');

const capFirst = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const normAbbr = (s) => capFirst(String(s).replace(/\.$/, ''));

// wiki photo dates come without a time: with it they are parsed as local time, not as UTC midnight
const PARSE_AS_LOCAL_TIME = 'T00:00';

const OPENING_HOURS_TAG_LOCALE = 'en-US';
const OPENING_HOURS_RULE_SEPARATOR = ';';
const OPENING_HOURS_COMMENT_SEPARATOR = ' - ';
const WORD_SEPARATORS = /([^A-Za-z]+)/;

const OPENING_HOURS_STRING_KEYS = {
    PH: 'opening_hours_public_holiday',
    SH: 'opening_hours_school_holiday',
    Easter: 'opening_hours_easter',
    off: 'day_off_label',
};

export const fmt = {
    // "MMM d, yyyy" (e.g., "Sep 4, 2025")
    MMMdY: (d) =>
        new Date(d).toLocaleDateString(lang(), {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        }),

    // "d MMMM yyyy" (e.g., "4 September 2025")
    dMMMMY: (d) =>
        new Date(d).toLocaleDateString(lang(), {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
        }),

    // "dd.MM.yyyy" (e.g., "04.09.2025")
    ddMMyyyy: (d) =>
        new Date(d).toLocaleDateString(lang(), {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        }),

    // "dd.MM" (e.g., "04.09")
    ddMM: (d) =>
        new Date(d).toLocaleDateString(lang(), {
            day: '2-digit',
            month: '2-digit',
        }),

    // "d MMMM" without year (e.g., "4 September", "4 вересня")
    dMMMM: (d) =>
        new Date(d).toLocaleDateString(lang(), {
            day: 'numeric',
            month: 'long',
        }),

    // Short weekday, capitalized, without trailing dot (e.g., "Mon", "Пн")
    wkShort: (d) => normAbbr(new Date(d).toLocaleDateString(lang(), { weekday: 'short' })),

    // Long weekday, capitalized (e.g., "Monday", "Понедельник")
    wkLong: (d) => capFirst(new Date(d).toLocaleDateString(lang(), { weekday: 'long' })),

    // "MMMM yyyy", capitalized (e.g., "September 2025")
    monthYearLong: (d) => capFirst(new Date(d).toLocaleDateString(lang(), { month: 'long', year: 'numeric' })),

    // 24h time "HH:mm"
    time24: (d) =>
        new Date(d).toLocaleTimeString(lang(), {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        }),

    // "MMM d" with month first (e.g., "Sep 13"; uk: "серп. 13")
    monthShortDay: (d) => {
        const date = new Date(d);
        const mon = date.toLocaleDateString(lang(), { month: 'short' });
        const day = date.toLocaleDateString(lang(), { day: 'numeric' });
        return `${mon} ${day}`;
    },

    // "MMM d, yyyy – HH:mm"
    dateTimeShort: (d) =>
        `${new Date(d).toLocaleDateString(lang(), {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        })} – ${new Date(d).toLocaleTimeString(lang(), {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        })}`,

    // "<Weekday>, HH:mm"
    wkLongTime: (d) =>
        `${capFirst(new Date(d).toLocaleDateString(lang(), { weekday: 'long' }))}, ${new Date(d).toLocaleTimeString(
            lang(),
            { hour: '2-digit', minute: '2-digit', hour12: false }
        )}`,
};

export function formatPhotoDate(dateStr) {
    if (!dateStr) {
        return '';
    }
    const clean = dateStr.startsWith('+') ? dateStr.slice(1) : dateStr;
    // Format YYYY 2025 -> 2025
    if (/^\d{4}$/.test(clean)) {
        return clean;
    }
    // Format YYYY-MM 2025-09 -> September 2025
    if (/^\d{4}-\d{2}$/.test(clean)) {
        const d = new Date(clean + '-01' + PARSE_AS_LOCAL_TIME); // Add the first day of the month for browser compatibility
        return isNaN(d) ? dateStr : fmt.monthYearLong(d);
    }
    // Format YYYY-MM-DD 2025-09-04 -> 4 September 2025
    const d = new Date(/^\d{4}-\d{2}-\d{2}$/.test(clean) ? clean + PARSE_AS_LOCAL_TIME : clean);

    return isNaN(d) ? dateStr : fmt.dMMMMY(d);
}

// Replace "Mo/Tu/We/Th/Fr/Sa/Su", month, holiday and "off" tokens with localized names
export function localizeOpeningHours(scheduleStr) {
    if (!scheduleStr) return scheduleStr;

    const now = new Date();
    const day = (now.getDay() + 6) % 7;
    const base = new Date(now);
    base.setHours(0, 0, 0, 0);
    base.setDate(now.getDate() - day);

    const dayName = (offset, locale) => {
        const d = new Date(base);
        d.setDate(base.getDate() + offset);
        return normAbbr(d.toLocaleDateString(locale, { weekday: 'short' }));
    };
    const monthName = (month, locale) =>
        new Date(now.getFullYear(), month).toLocaleDateString(locale, { month: 'short' });

    const names = new Map();
    for (let offset = 0; offset < 7; offset++) {
        const token = dayName(offset, OPENING_HOURS_TAG_LOCALE);
        const name = dayName(offset, lang());
        names.set(token, name);
        names.set(token.slice(0, 2), name);
    }
    for (let month = 0; month < 12; month++) {
        names.set(monthName(month, OPENING_HOURS_TAG_LOCALE), monthName(month, lang()));
    }
    Object.entries(OPENING_HOURS_STRING_KEYS).forEach(([token, key]) => names.set(token, i18n.t(key)));

    const localizeWords = (text) =>
        text
            .split(WORD_SEPARATORS)
            .map((word) => names.get(word) ?? word)
            .join('');

    return scheduleStr
        .split(OPENING_HOURS_RULE_SEPARATOR)
        .map((rule) => {
            const commentStart = rule.indexOf(OPENING_HOURS_COMMENT_SEPARATOR);
            const scheduleEnd = commentStart < 0 ? rule.length : commentStart;

            return localizeWords(rule.slice(0, scheduleEnd)) + rule.slice(scheduleEnd);
        })
        .join(OPENING_HOURS_RULE_SEPARATOR);
}
